import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { EmailTemplate, EmailTemplateFormData } from '@/types/emailTemplate';

export const useEmailTemplates = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch all templates
  const fetchTemplates = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const { data, error: fetchError } = await supabase
        .from('email_templates')
        .select('*')
        .order('display_order', { ascending: true });

      if (fetchError) throw fetchError;
      
      setTemplates(data || []);
    } catch (err) {
      console.error('Error fetching email templates:', err);
      setError('Kunne ikke laste e-postmaler');
      toast.error('Kunne ikke laste e-postmaler');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Update a template
  const updateTemplate = async (id: string, updates: Partial<EmailTemplateFormData>) => {
    try {
      setIsSaving(true);
      
      const { error: updateError } = await supabase
        .from('email_templates')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (updateError) throw updateError;
      
      // Update local state
      setTemplates(prev => prev.map(t => 
        t.id === id ? { ...t, ...updates, updated_at: new Date().toISOString() } : t
      ));
      
      toast.success('Mal lagret');
      return true;
    } catch (err) {
      console.error('Error updating email template:', err);
      toast.error('Kunne ikke lagre malen');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Set a template as default
  const setDefaultTemplate = async (id: string) => {
    try {
      setIsSaving(true);
      
      // First, unset all defaults
      const { error: unsetError } = await supabase
        .from('email_templates')
        .update({ is_default: false })
        .neq('id', id);

      if (unsetError) throw unsetError;
      
      // Then set the new default
      const { error: setError } = await supabase
        .from('email_templates')
        .update({ is_default: true })
        .eq('id', id);

      if (setError) throw setError;
      
      // Update local state
      setTemplates(prev => prev.map(t => ({
        ...t,
        is_default: t.id === id
      })));
      
      toast.success('Standard mal oppdatert');
      return true;
    } catch (err) {
      console.error('Error setting default template:', err);
      toast.error('Kunne ikke sette standard mal');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Get default template
  const getDefaultTemplate = useCallback(() => {
    return templates.find(t => t.is_default) || templates[0];
  }, [templates]);

  // Get template by ID
  const getTemplateById = useCallback((id: string) => {
    return templates.find(t => t.id === id);
  }, [templates]);

  return {
    templates,
    isLoading,
    isSaving,
    error,
    fetchTemplates,
    updateTemplate,
    setDefaultTemplate,
    getDefaultTemplate,
    getTemplateById
  };
};
